import { useMemo } from "react";

import { ActionType, ChangelogEntry, EntityType, useGetChangelog } from "@/queries/folder-entities";

interface PendingLabelMove {
  changelogId: number;
  labelName: string;
  labelId: number;
  fromVersionNumber: number;
  toVersionNumber: number;
  datetime: string;
}

interface PendingLabelDelete {
  changelogId: number;
  labelName: string;
  labelId: number;
  datetime: string;
}

interface PendingLabelMovesByVersion {
  incoming: PendingLabelMove[];
  outgoing: PendingLabelMove[];
}

export type PendingReleaseLabelMovesRegistry = Record<number, PendingLabelMovesByVersion>;

export type PendingDeletesByLabelId = Map<number, PendingLabelDelete>;

interface UsePendingReleaseLabelMovesResult {
  pendingMoves: PendingReleaseLabelMovesRegistry;
  pendingDeletesByLabelId: PendingDeletesByLabelId;
  isLoading: boolean;
}

const isMoveLabelEntry = (entry: ChangelogEntry): entry is ChangelogEntry<ActionType.MOVE_LABEL> =>
  entry.action_type === ActionType.MOVE_LABEL;

const isDeleteLabelEntry = (
  entry: ChangelogEntry,
): entry is ChangelogEntry<ActionType.DELETE_LABEL> =>
  entry.action_type === ActionType.DELETE_LABEL;

const usePendingReleaseLabelMoves = (
  entityId: number | undefined,
  entityType: EntityType,
  enabled = true,
): UsePendingReleaseLabelMovesResult => {
  const { data, isLoading } = useGetChangelog({
    entity_id: entityId ?? 0,
    entity_type: entityType,
    pending: true,
    per_page: 100,
    enabled: enabled && !!entityId,
  });

  const { pendingMoves, pendingDeletesByLabelId } = useMemo(() => {
    const registry: PendingReleaseLabelMovesRegistry = {};
    const deletesByLabelId: PendingDeletesByLabelId = new Map();

    if (!data?.pages) {
      return { pendingMoves: registry, pendingDeletesByLabelId: deletesByLabelId };
    }

    const allPendingEntries = data.pages.flatMap(page => page.items);

    allPendingEntries.forEach(entry => {
      if (!isMoveLabelEntry(entry) || entry.merged || !entry.action_payload) {
        return;
      }

      const payload = entry.action_payload;

      let fromVersionNumber: number | undefined;
      let toVersionNumber: number | undefined;
      let labelId: number | undefined;
      let labelName: string | undefined;

      if ("old_prompt_template_version_number" in payload) {
        fromVersionNumber = payload.old_prompt_template_version_number;
        toVersionNumber = payload.new_prompt_template_version_number;
        labelId = payload.prompt_label_id;
        labelName = payload.label_name;
      } else if ("old_workflow_version_number" in payload) {
        fromVersionNumber = payload.old_workflow_version_number;
        toVersionNumber = payload.new_workflow_version_number;
        labelId = payload.workflow_label_id;
        labelName = payload.label_name;
      } else if ("old_skill_collection_version_number" in payload) {
        fromVersionNumber = payload.old_skill_collection_version_number;
        toVersionNumber = payload.new_skill_collection_version_number;
        labelId = payload.skill_collection_label_id;
        labelName = payload.label_name;
      } else if ("old_tool_version_number" in payload) {
        fromVersionNumber = payload.old_tool_version_number;
        toVersionNumber = payload.new_tool_version_number;
        labelId = payload.tool_label_id;
        labelName = payload.label_name;
      }

      if (
        fromVersionNumber === undefined ||
        toVersionNumber === undefined ||
        labelId === undefined ||
        !labelName
      ) {
        return;
      }

      const pendingMove: PendingLabelMove = {
        changelogId: entry.id,
        labelName,
        labelId,
        fromVersionNumber,
        toVersionNumber,
        datetime: entry.datetime,
      };

      if (!registry[toVersionNumber]) {
        registry[toVersionNumber] = { incoming: [], outgoing: [] };
      }
      registry[toVersionNumber].incoming.push(pendingMove);

      if (!registry[fromVersionNumber]) {
        registry[fromVersionNumber] = { incoming: [], outgoing: [] };
      }
      registry[fromVersionNumber].outgoing.push(pendingMove);
    });

    allPendingEntries.forEach(entry => {
      if (!isDeleteLabelEntry(entry) || entry.merged || !entry.action_payload) {
        return;
      }

      const payload = entry.action_payload;

      let labelId: number | undefined;
      let labelName: string | undefined;

      if ("prompt_template_version_number" in payload) {
        labelId = payload.prompt_label_id;
        labelName = payload.label_name;
      } else if ("workflow_version_number" in payload) {
        labelId = payload.workflow_label_id;
        labelName = payload.label_name;
      } else if ("skill_collection_version_number" in payload) {
        labelId = payload.skill_collection_label_id;
        labelName = payload.label_name;
      } else if ("tool_version_number" in payload) {
        labelId = payload.tool_label_id;
        labelName = payload.label_name;
      }

      if (labelId === undefined || !labelName) {
        return;
      }

      const pendingDelete: PendingLabelDelete = {
        changelogId: entry.id,
        labelName,
        labelId,
        datetime: entry.datetime,
      };

      deletesByLabelId.set(labelId, pendingDelete);
    });

    return { pendingMoves: registry, pendingDeletesByLabelId: deletesByLabelId };
  }, [data]);

  return {
    pendingMoves,
    pendingDeletesByLabelId,
    isLoading,
  };
};

export default usePendingReleaseLabelMoves;
